import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import type {
  AccountCampaign,
  AccountCampaignBulkAction,
} from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';
import type { CurrentUser } from '../../types/user';
import { resolveBulkRefreshTarget } from './bulkRefreshGate';

// P2b-3 §7: bulk execution-checkbox bar shared between the Meta and
// Google grids. Limited to the "Mark X" direction per spec — un-tick is
// per-row only. Hidden when zero rows selected. Hidden entirely when the
// rollout is archived (master grid does the same).
//
// The bar is sticky and floats above the grid header, matching the master
// grid's BulkActionBar styling so users moving between views aren't
// retraining muscle memory.
//
// Round-1 polish Fix 6: + "Assign builder…" affordance (mirrors master
// grid's "Assign AM…" pattern). Reveals an inline dropdown with all
// users + an explicit "— Unassign —" option; click Apply to dispatch.

type ActionDef = {
  label: string;
  kind: AccountCampaignBulkAction['kind'];
};

// Round-3 polish Fix 3: Meta "Live" → "Scheduled/Live"; Google
// "Post-Check Done" → "Budget Updated". Wire kinds untouched.
const META_ACTIONS: ActionDef[] = [
  { label: 'Mark Main Built', kind: 'setMetaBuilt' },
  { label: 'Mark Scheduled/Live', kind: 'setMetaTurnedOn' },
  { label: 'Mark Post-Check Done', kind: 'setMetaPostRolloutCheck' },
];

const GOOGLE_ACTIONS: ActionDef[] = [
  { label: 'Mark Main Built', kind: 'setGoogleBuilt' },
  { label: 'Mark Scheduled', kind: 'setGoogleScheduled' },
  { label: 'Mark Budget Updated', kind: 'setGooglePostRolloutCheck' },
];

type Props = {
  platform: 'meta' | 'google';
  selectedIds: string[];
  onClear: () => void;
  onDispatch: (action: AccountCampaignBulkAction) => void;
  archived: boolean;
  // Round-1 polish Fix 6: users list for the "Assign builder…"
  // dropdown. Optional so callers that haven't yet wired it (none
  // currently, but future surfaces) skip the affordance gracefully.
  users?: CurrentUser[];
  // Granular Refresh spec §1. The Meta/Google grids surface the SAME
  // bulk refresh action as the master grid. Optional so the affordance
  // only renders where the grid passes its rows + campaigns.
  rows?: AccountCampaign[];
  campaigns?: Campaign[];
  onRefreshFromCampaign?: (campaign: Campaign) => void;
};

export function GridBulkExecutionBar({
  platform,
  selectedIds,
  onClear,
  onDispatch,
  archived,
  users,
  rows,
  campaigns,
  onRefreshFromCampaign,
}: Props) {
  const [mode, setMode] = useState<null | 'assignBuilder'>(null);
  const [pickedBuilder, setPickedBuilder] = useState<string>('');

  // Granular Refresh spec §1. Same single-Campaign gate as the master
  // grid. Only meaningful when the grid wired rows + campaigns.
  const refreshGate = useMemo(
    () =>
      rows && campaigns
        ? resolveBulkRefreshTarget(selectedIds, rows, campaigns)
        : { target: null, reason: null },
    [selectedIds, rows, campaigns],
  );

  // Reset the inline assign-builder mode when the bar hides (selection
  // emptied or rollout archived) — otherwise re-selecting rows would
  // re-show the dropdown in its previously-open state, which is jarring.
  useEffect(() => {
    if (archived || selectedIds.length === 0) {
      setMode(null);
      setPickedBuilder('');
    }
  }, [archived, selectedIds.length]);

  if (archived || selectedIds.length === 0) return null;
  const actions = platform === 'meta' ? META_ACTIONS : GOOGLE_ACTIONS;
  const builderKind: AccountCampaignBulkAction['kind'] =
    platform === 'meta' ? 'setMetaBuilder' : 'setGoogleBuilder';

  return (
    <div className="sticky top-12 z-20 mb-3 rounded-md border border-slate-300 bg-white shadow-lg p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-800">
          {selectedIds.length} selected
        </span>
        <span className="text-slate-300">|</span>
        {actions.map((a) => (
          <Button
            key={a.kind}
            variant="outline"
            onClick={() =>
              // The discriminated union narrowing here would require a
              // switch — every variant has the same shape (boolean value
              // on the same accountCampaignIds), so a typed cast keeps
              // the call site one-liner.
              onDispatch({
                kind: a.kind,
                accountCampaignIds: selectedIds,
                value: true,
              } as AccountCampaignBulkAction)
            }
          >
            {a.label}
          </Button>
        ))}
        {users && (
          <Button
            variant="outline"
            onClick={() => {
              setMode((m) => (m === 'assignBuilder' ? null : 'assignBuilder'));
              setPickedBuilder('');
            }}
          >
            Assign builder…
          </Button>
        )}
        {onRefreshFromCampaign && rows && campaigns && (
          <Button
            variant="outline"
            disabled={refreshGate.target === null}
            onClick={() =>
              refreshGate.target && onRefreshFromCampaign(refreshGate.target)
            }
            title={refreshGate.reason ?? undefined}
          >
            Refresh from Campaign…
          </Button>
        )}
        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-sm text-slate-500 hover:text-slate-800 underline"
        >
          Clear selection
        </button>
      </div>

      {mode === 'assignBuilder' && users && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <select
            value={pickedBuilder}
            onChange={(e) => setPickedBuilder(e.target.value)}
            aria-label="Builder"
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">— Unassign —</option>
            {users.map((u) => (
              <option key={u.uid} value={u.uid}>
                {formatUserDisplayName(u)}
              </option>
            ))}
          </select>
          <Button
            onClick={() => {
              onDispatch({
                kind: builderKind,
                accountCampaignIds: selectedIds,
                builderId: pickedBuilder || null,
              } as AccountCampaignBulkAction);
              setMode(null);
              setPickedBuilder('');
            }}
          >
            Apply
          </Button>
          <Button variant="outline" onClick={() => setMode(null)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
